import { Icon } from "@sagot/ui";
import { useCallback, useId, useMemo, useState } from "react";
import { CARD_CLASS, renderOutcome } from "./card-shell";
import { NO_FIELDS } from "./constants";
import type { ActionCardHostProps } from "./card-props";
import { useCaptcha } from "./captcha";
import type { BookTourPayload, ConfirmResponse, ResultCard, Slot } from "./contract";
import { formatDate, formatTimeRange, visitorTimeZone, zoneName } from "./dates";
import { FailureNotice } from "./failure-notice";
import { ActionFieldsForm } from "./fields-form";
import { useActionLabels, type ActionLabels } from "./labels";
import { SlotPicker } from "./slot-picker";
import { useConfirmFlow } from "./use-confirm-flow";
import { useSlots, type FetchSlots } from "./use-slots";

export interface BookingCardProps extends ActionCardHostProps {
  payload: BookTourPayload;
  /** `GET /public/v1/actions/{run_id}/slots` bound to this card's run. */
  loadSlots?: FetchSlots | undefined;
}

function Header({ id, payload, labels }: { id: string; payload: BookTourPayload; labels: ActionLabels }) {
  const intro = payload.mode === "calendar" ? labels.calendarIntro : labels.requestIntro;
  return (
    <div className="space-y-1">
      <div id={id} className="flex items-center gap-2 text-xs font-bold text-fg">
        <Icon name="calendar" className="size-4 text-primary-ink" />
        {payload.title}
      </div>
      <p className="text-xs text-fg-muted">{intro}</p>
      {payload.duration_minutes || payload.location ? (
        <p className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-fg-subtle">
          {payload.duration_minutes ? (
            <span className="inline-flex items-center gap-1">
              <Icon name="clock" className="size-3" />
              {labels.minutes(payload.duration_minutes)}
            </span>
          ) : null}
          {payload.location ? (
            <span className="inline-flex items-center gap-1">
              <Icon name="map-pin" className="size-3" />
              {payload.location}
            </span>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}

function SelectedTime({ slot, timeZone, locale, label }: { slot: Slot; timeZone: string; locale?: string | undefined; label: string }) {
  return (
    <p role="status" className="flex items-start gap-1.5 rounded-md bg-primary-soft p-2.5 text-xs font-semibold text-fg">
      <Icon name="calendar" className="mt-0.5 size-3.5 text-primary-ink" />
      <span>
        <span className="sr-only">{label}: </span>
        {formatDate(slot.start, timeZone, locale)} · {formatTimeRange(slot.start, slot.end, timeZone, locale)} {zoneName(slot.start, timeZone, locale)}
      </span>
    </p>
  );
}

function bookingFallback(payload: BookTourPayload): (response: ConfirmResponse) => ResultCard {
  return (response) => ({ kind: "booking", state: payload.mode === "calendar" ? "confirmed" : "requested", title: response.title, body: response.body });
}

/** Offered booking: pick a time (slots endpoint) → details → "Request this time" / "Book this time". */
export function BookingCard({ payload, loadSlots, confirm, onStateChange, renderCaptcha, labels: overrides, locale, timeZone: zone, preview, showMeta }: BookingCardProps) {
  const id = useId();
  const labels = useActionLabels(overrides);
  const timeZone = useMemo(() => zone ?? visitorTimeZone(), [zone]);
  const slots = useSlots({ fetchSlots: preview ? undefined : loadSlots, timeZone });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const day = slots.days.find((d) => d.slots.some((slot) => slot.id === selectedId));
  const selected = day?.slots.find((slot) => slot.id === selectedId);
  const { reloadDay } = slots;
  const onSlotUnavailable = useCallback(() => {
    setSelectedId(null);
    if (day) reloadDay(day.date);
  }, [day, reloadDay]);
  const fallbackResult = useMemo(() => bookingFallback(payload), [payload]);
  const flow = useConfirmFlow({ confirm, labels, timeZone, fallbackResult, onStateChange, onSlotUnavailable });
  const captcha = useCaptcha(renderCaptcha, flow.attempts);

  const outcome = renderOutcome({ result: flow.result ?? payload.result, expired: flow.expired, title: payload.title, labels, timeZone, locale, live: true });
  if (outcome) return outcome;

  const submitLabel = payload.submit_label ?? (payload.mode === "calendar" ? labels.bookThisTime : labels.requestThisTime);
  const notice = flow.failure ? (
    <FailureNotice failure={flow.failure} labels={labels} submitting={flow.submitting} onRetry={() => void flow.retry({ captchaToken: captcha.take() })} />
  ) : null;
  return (
    <div role="group" aria-labelledby={id} className={CARD_CLASS}>
      <Header id={id} payload={payload} labels={labels} />
      {selected ? null : notice}
      {preview ? (
        <p className="text-xs text-fg-muted">{labels.previewTimes}</p>
      ) : (
        <SlotPicker slots={slots} timeZone={timeZone} locale={locale} selectedId={selected?.id ?? null} onSelect={(slot) => { setSelectedId(slot.id); }} labels={labels} disabled={flow.submitting} />
      )}
      {selected ? <SelectedTime slot={selected} timeZone={timeZone} locale={locale} label={labels.yourTime} /> : null}
      {selected || preview ? (
        <ActionFieldsForm
          ariaLabel={labels.detailsHeading}
          fields={payload.fields}
          consentText={payload.consent_text}
          privacyUrl={payload.privacy_url}
          submitLabel={submitLabel}
          submitting={flow.submitting}
          disabled={preview}
          labels={labels}
          rejected={flow.failure?.fields ?? NO_FIELDS}
          notice={notice}
          captcha={captcha.node}
          onSubmit={(values) => void flow.submit({ values, slotId: selected?.id, captchaToken: captcha.take() })}
        />
      ) : null}
      {preview ? <p className="text-[11px] font-semibold text-fg-subtle">{labels.previewNote}</p> : null}
      {showMeta ? (
        <p className="text-[11px] text-fg-subtle">
          Action: <span className="font-mono">{payload.action_id}</span> → {payload.integration ?? payload.mode}
        </p>
      ) : null}
    </div>
  );
}
